// react_frontend/src/Components/Modules/ItemSetting/ItemList.js
import React, { useState, useMemo, useContext, useEffect } from "react";
import { MotherContext } from "../../../Context/MotherContext";
import Breadcrumb from "../../Partials/Breadcrumb";
import DataTable from "react-data-table-component";
import { AxiosAuthInstance } from "../../../AxiosInterceptors";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";

// RSuite Components
import {
  Button,
  Input,
  Badge,
  Loader,
  Whisper,
  Tooltip,
  Message,
  toaster,
} from "rsuite";
import "rsuite/dist/rsuite.min.css";

// Icons
import {
  FaBox,
  FaTags,
  FaUser,
  FaToggleOn,
  FaToggleOff,
  FaEdit,
  FaTrashAlt,
  FaPlus,
  FaSearch,
} from "react-icons/fa";
import moment from "moment";

const ItemList = () => {
  const { items, loadingItems, updateItemInList, removeItemFromList, startItemPolling } = useContext(MotherContext);
  const [search, setSearch] = useState("");

  const isAdminOrAccount = ["1", "2"].includes(localStorage.getItem("who_am_i"));
  const isMasterAdmin = localStorage.getItem("who_am_i") === "1";

  // Fetch Data
  useEffect(() => {
    startItemPolling();
  }, [startItemPolling]);


  // Filtered Data
  const filteredData = useMemo(() => {
    return items
      .filter(item => item && (
        (item.category_name || "").toLowerCase().includes(search.toLowerCase()) ||
        (item.item_name || "").toLowerCase().includes(search.toLowerCase())
      ))
      .sort((a, b) => b.id - a.id);
  }, [items, search]);

  // Handle Delete
  const handleDelete = (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to recover this item!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "No, cancel",
      reverseButtons: true,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.delete(`/add_item/${item.id}`);
          removeItemFromList(item.id);
          toaster.push(
            <Message type="success" duration={3000}>
              Item deleted successfully!
            </Message>,
            { placement: "topEnd" }
          );
        } catch (err) {
          toaster.push(
            <Message type="error" duration={5000}>
              Failed to delete item.
            </Message>,
            { placement: "topEnd" }
          );
        }
      }
    });
  };

  // Handle Status Toggle
  const handleStatusToggle = (item) => {
    const newStatus = item.item_status === 1 ? 0 : 1;

    Swal.fire({
      title: `Are you sure?`,
      text: `You will mark "${item.item_name}" as ${newStatus ? "Active" : "Inactive"}.`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: `Yes, ${newStatus ? "Activate" : "Deactivate"}`,
      cancelButtonText: "Cancel",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await AxiosAuthInstance.post(`/update_item_status/${item.id}/${newStatus}`);
          updateItemInList({ ...item, item_status: newStatus });

          toaster.push(
            <Message type="success" duration={3000}>
              Item status updated successfully!
            </Message>,
            { placement: "topEnd" }
          );
        } catch (err) {
          console.error("Status update error:", err);
          updateItemInList(item);
          let errorMessage = "Failed to update item status. Please try again.";

          if (err.response?.data?.message) {
            errorMessage = err.response.data.message;
          }

          toaster.push(
            <Message type="error" duration={5000}>
              {errorMessage}
            </Message>,
            { placement: "topEnd" }
          );
        }
      }
    });
  };
  // Columns
  const columns = [
    {
      name: "SL",
      cell: (row, index) => <strong>{index + 1}</strong>,
      width: "60px",
      center: true,
    },
    {
      name: "Category",
      selector: (row) => row.category_name,
      sortable: true,
      width: "200px",
      cell: (row) => (
        <Badge color="blue" style={{ fontSize: "0.85em" }}>
          <FaTags /> {row.category_name}
        </Badge>
      ),
    },
    {
      name: "Item Name",
      selector: (row) => row.item_name,
      sortable: true,
      width: "400px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaBox className="text-primary" />
          <span className="fw-medium">{row.item_name} </span>
        </div>
      ),
    },
    {
      name: "Unit",
      selector: (row) => row.product_unit,
      sortable: true,
      width: "100px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-2">
          <FaBox className="text-primary" />
          <span className="fw-medium">{row.product_unit} </span>
        </div>
      ),
    },
    {
      name: "Status",
      width: "110px",
      center: true,
      cell: (row) => (
        <Badge
          color={row.item_status ? "green" : "red"}
          content={row.item_status ? "Active" : "Inactive"}
          style={{ fontSize: "0.8em" }}
        />
      ),
    },
    {
      name: "Actions",
      cell: (row) =>
        isAdminOrAccount ? (
          <div className="d-flex gap-1 justify-content-center">
            {/* Status Toggle */}
            <Whisper
              placement="top"
              speaker={
                <Tooltip>{row.item_status ? "Deactivate" : "Activate"}</Tooltip>
              }
            >
              <Button
                size="sm"
                color={row.item_status ? "green" : "red"}
                startIcon={row.item_status ? <FaToggleOn /> : <FaToggleOff />}
                onClick={() => handleStatusToggle(row)}
              >
                {row.item_status ? "Active" : "Inactive"}
              </Button>
            </Whisper>

            {/* Edit */}
            <Whisper placement="top" speaker={<Tooltip>Edit item</Tooltip>}>
              <Link to={`/edit_item/${row.id}`} style={{ textDecoration: "none" }}>
                <Button size="sm" color="blue" startIcon={<FaEdit />}>
                  Edit
                </Button>
              </Link>
            </Whisper>

            {/* Delete (Master Admin only) */}
            {/* {isMasterAdmin && (
              <Whisper placement="top" speaker={<Tooltip>Delete item</Tooltip>}>
                <Button
                  size="sm"
                  color="red"
                  startIcon={<FaTrashAlt />}
                  onClick={() => handleDelete(row)}
                >
                  Delete
                </Button>
              </Whisper>
            )} */}
          </div>
        ) : (
          <Badge color="default">View Only</Badge>
        ),
      width: "280px",
      center: true,
    },
    {
      name: "Created By",
      selector: (row) => row.creator_name,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <div className="d-flex align-items-center gap-1">
          <FaUser className="text-secondary" />
          <small>{row.creator_name}</small>
        </div>
      ),
    },
    {
      name: "Updated By",
      selector: (row) => row.updator_name,
      sortable: true,
      width: "140px",
      cell: (row) => (
        <small>
          {row.updator_name ? row.updator_name : <span className="text-muted">Not updated</span>}
        </small>
      ),
    },
  ];


  // Custom Styling
  const customStyles = {
    head: {
      style: {
        fontSize: "14px",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
      },
    },
    rows: {
      style: {
        fontSize: "14px",
        minHeight: "60px",
      },
    },
    cells: {
      style: {
        padding: "12px 8px",
      },
    },
  };

  const AddItemButton = () =>
    isAdminOrAccount ? (
      <Whisper placement="top" speaker={<Tooltip>Add new item</Tooltip>}>
        <Link to="/add_item" style={{ textDecoration: "none" }}>
          <Button
            appearance="primary"
            startIcon={<FaPlus />}
            className="d-flex align-items-center"
          >
            Add Item
          </Button>
        </Link>
      </Whisper>
    ) : null;

  return (
    <>
      <Breadcrumb title="Item Management" />

      <div className="p-3 bg-white rounded shadow-sm mb-4 d-flex flex-wrap gap-3 align-items-center justify-content-between">
        <h5 className="text-dark mb-0">
          <FaBox className="me-2" />
          Inventory Items
        </h5>

        <div className="d-flex gap-2 w-100 w-md-auto">
          <Input
            placeholder="Search by category or item name..."
            value={search}
            onChange={setSearch}
            prefix={<FaSearch />}
            style={{ width: 300 }}
            size="md"
          />
          <AddItemButton />
        </div>
      </div>

      <DataTable
        keyField="id"
        columns={columns}
        data={filteredData}
        pagination
        paginationPerPage={100}
        paginationRowsPerPageOptions={[100, 250, 500, 1000]}
        progressPending={loadingItems}
        progressComponent={
          <div className="d-flex justify-content-center my-5">
            <Loader size="md" /> <span className="ms-2">Loading items...</span>
          </div>
        }
        noDataComponent={
          <div className="text-center my-5 text-muted">
            <FaBox size={48} className="mb-3 text-secondary" />
            <h5>No items found</h5>
            <p>Try adjusting your search or add a new item.</p>
          </div>
        }
        highlightOnHover
        responsive
        fixedHeader
        fixedHeaderScrollHeight="600px"
        customStyles={customStyles}
        striped
      />
    </>
  );
};

export default ItemList;